<template>
    <ContentComp
        ref="contentComp"
        class="h-100"
        :load-state="LoadState"
    >
        <LoadingComp
            v-if="showImportModal"
            ref="loadingComp"
            :state="LoadStateImport"
            :confirm-close="true"
            :show-as-modal="true"
            @closed="closeImportLoadingComp"
            class="mt-4"
        >
            <div class="w-abs-300 text-center mb-5">
                <p v-html="loadingCompMessage"></p>
                <div v-if="loadingCompFiles?.length > 0">
                    <div
                        class="d-flex flex-row justify-content-center flex-wrap"
                        v-for="file of loadingCompFiles"
                        :key="file.FileGuid"
                    >
                        <DisplayMimeType
                            v-if="file.MimeType"
                            :value="file.MimeType"
                            :showText="false"
                        />
                        <DisplayFilename
                            :row="file"
                            :value="null"
                        />
                    </div>
                </div>
            </div>
        </LoadingComp>

        <!-- :confirm-modal-close="confirmModalClose"
            @loadingModalClosed="confirmModalClose = false" -->

        <template
            v-if="
                (currentImportSource == 'event' || currentImportSource == 'external') &&
                !isImportRowsLoaded &&
                (sourceEvent?.ID != null || externalEventData != null)
            "
        >
            <!-- If we have a sourceEvent, but no rows loaded we will show the raceLayoutmapper -->
            <RaceLayoutMapper
                :source-event-id="sourceEvent?.ID"
                :externalEventData="externalEventData"
                @complete="importfromsource"
            />
        </template>

        <!-- The view where we select the source. File, Previous race or Isonen -->
        <template v-if="isImportRowsLoaded == null && currentImportSource == 'none'">
            <div class="h-100 overflow-auto m-n3 p-3">
                <AlertMessage
                    type="danger"
                    v-if="currentException != null"
                    ><span v-html="$tm(currentException, '', true)"></span
                ></AlertMessage>

                <!-- We show the filedrop and the source event search if we dont have a sourceEvent set -->
                <MissingRacesMessage :gotoracepage="true" />

                <div class="d-flex gap-5 flex-column">
                    <Dropzone
                        ref="dropzone"
                        class="h-100 border border-2 h-max-200"
                        @change="_onFileListChanged"
                    ></Dropzone>

                    <h4
                        class="display-4"
                        v-html="$tm('participant.importfromsource', '', true)"
                    ></h4>
                    <div class="d-flex justify-content-center">
                        <div>
                            <ContestantCopyFromEvent
                                v-model="sourceEvent"
                                v-model:externalData="externalEventData"
                            >
                                <span
                                    class="text-center"
                                    v-html="$tm('participant.importfromsourcetext', '', true)"
                                ></span>
                            </ContestantCopyFromEvent>
                            <div
                                class="d-flex flex-column gap-2"
                                v-if="sourceEvent != null || externalEventData != null"
                            >
                                <AlertMessage
                                    class="flex-fill"
                                    innerclass="flex-fill"
                                    v-if="
                                        (sourceEvent != null && sourceEvent?.NumContestants == 0) ||
                                        (externalEventData != null && externalEventData.Contestants.length == 0)
                                    "
                                    type="warning"
                                    >{{ $tm('contestantimport.nocontestantinsource') }}</AlertMessage
                                >
                                <div class="d-flex justify-content-center">
                                    <ButtonComp
                                        button-type="Primary"
                                        icon="fa-check"
                                        :disabled="
                                            (sourceEvent != null && sourceEvent?.NumContestants == 0) ||
                                            (externalEventData != null && externalEventData.Contestants.length == 0)
                                        "
                                        @click="currentImportSource = sourceEvent !== null ? 'event' : 'external'"
                                        class="w-min-150"
                                        >{{ $tm('button.confirm') }}</ButtonComp
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <!-- The actual table view with all the options and grid -->
        <template v-if="isImportRowsLoaded">
            <form
                class="row row-cols-lg-auto g-3 align-items-center needs-validation"
                v-show="!contestantImportDefaultSettings || contestantImportDefaultSettings.ShowToolbar"
                novalidate
            >
                <div class="col-12 col-lg-2">
                    <label> </label>
                    <div class="d-flex gap-0 me-5">
                        <DropdownSelect
                            class="flex-fill"
                            caption="Select a format"
                            :data="computedFormats"
                            v-model="selectedFormat"
                        />
                        <ButtonComp
                            buttonType="Add"
                            :title="$tm('participantimport.newformat')"
                            @click="newFormat"
                        ></ButtonComp>
                        <ButtonComp
                            v-if="false"
                            buttonType="Save"
                            :title="$tm('participantimport.saveformat')"
                            @click="saveCurrentFormat"
                            :disabled="selectedFormat == null"
                        ></ButtonComp>
                        <ButtonComp
                            buttonType="Delete"
                            :title="$tm('participantimport.deleteformat')"
                            @click="deleteSelectedFormat"
                            :disabled="selectedFormat == null"
                        ></ButtonComp>
                    </div>
                </div>
                <div class="col-12">
                    <label>{{ $tm('import.dateformat') }}<TooltipHelp text="import.dateformathelp" /></label>
                    <div class="input-group">
                        <div class="input-group-text"><IconComp icon="fa-calendar"></IconComp></div>
                        <input
                            @change="_revalidate('Date')"
                            type="text"
                            class="form-control"
                            v-model="dateFormatValue"
                            :placeholder="$tm('participantimport.defaultdateformat')"
                            pattern="(?=.*y)(?=.*m)(?=.*d).*"
                            spellcheck="false"
                        />
                    </div>
                </div>
                <div class="col-12">
                    <label>{{ $tm('import.timeformat') }}<TooltipHelp text="import.timeformathelp" /></label>
                    <div class="input-group">
                        <div class="input-group-text"><IconComp icon="fa-clock"></IconComp></div>
                        <input
                            type="text"
                            v-model="timeFormatValue"
                            @change="_revalidate('Time')"
                            class="form-control"
                            :placeholder="$tm('participantimport.defaulttimeformat')"
                        />
                    </div>
                </div>
                <div class="col-12">
                    <label>{{ $tm('import.gendermale') }}<TooltipHelp text="import.gendermalehelp" /></label>
                    <div class="input-group">
                        <div class="input-group-text"><IconComp icon="fa-mars"></IconComp></div>
                        <input
                            type="text"
                            @change="_revalidate('Gender')"
                            v-model="genderMaleValue"
                            class="form-control"
                            :placeholder="$tm('participantimport.defaultgendermale')"
                        />
                    </div>
                </div>

                <div class="col-12">
                    <label>{{ $tm('import.genderfemale') }}<TooltipHelp text="import.genderfemalehelp" /></label>
                    <div class="input-group">
                        <div class="input-group-text"><IconComp icon="fa-venus"></IconComp></div>
                        <input
                            type="text"
                            @change="_revalidate('Gender')"
                            v-model="genderFemaleValue"
                            class="form-control"
                            :placeholder="$tm('participantimport.defaultgenderfemale')"
                        />
                    </div>
                </div>

                <div class="col-12">
                    <label>{{ $tm('import.genderneutral') }}<TooltipHelp text="import.genderneutralhelp" /></label>
                    <div class="input-group">
                        <div class="input-group-text"><IconComp icon="fa-venus"></IconComp></div>
                        <input
                            type="text"
                            @change="_revalidate('Gender')"
                            v-model="genderNeutralValue"
                            class="form-control"
                            :placeholder="$tm('participantimport.defaultgenderneutral')"
                        />
                    </div>
                </div>
            </form>
            <div
                class="accordion"
                v-if="contestantImportDefaultSettings != null && !contestantImportDefaultSettings.HideAdvancedOptions"
            >
                <div class="accordion-item bg-transparent">
                    <h2 class="accordion-header display-3">
                        <button
                            class="accordion-button collapsed bg-transparent"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#importAdvancedSettings"
                            aria-expanded="false"
                            aria-controls="importAdvancedSettings"
                        >
                            <IconComp
                                icon="fa-gear"
                                class="me-3"
                            />
                            {{ $tm('import.advancedsettings') }}
                        </button>
                    </h2>
                    <div
                        id="importAdvancedSettings"
                        class="accordion-collapse collapse"
                    >
                        <div class="accordion-body">
                            <form class="row align-items-center">
                                <InputBool
                                    class="col-12 col-md-4 col-sm-6"
                                    v-model="contestantImport.RelayTeamsOnly"
                                    label="import.relayteamsonly"
                                    tooltip="import.relayteamsonlyhelp"
                                />
                                <InputBool
                                    class="col-12 col-md-4 col-sm-6"
                                    v-model="contestantImport.GenerateCategories"
                                    label="import.generatemissingclass"
                                    tooltip="import.generatemissingclasshelp"
                                />
                                <InputBool
                                    class="col-12 col-md-4 col-sm-6"
                                    v-model="contestantImport.GenerateGroups"
                                    label="import.generategroups"
                                    tooltip="import.generategroupshelp"
                                />
                                <InputBool
                                    class="col-12 col-md-4 col-sm-6"
                                    v-model="contestantImport.GenerateRacesForGroups"
                                    label="import.generateracesgroups"
                                    tooltip="import.generateracesgroupshelp"
                                />

                                <InputBool
                                    class="col-12 col-md-4 col-sm-6"
                                    v-model="contestantImport.OneTimeParticipant"
                                    label="import.onetimeparticipant"
                                    tooltip="import.onetimeparticipanthelp"
                                />
                                <InputBool
                                    class="col-12 col-md-4 col-sm-6"
                                    v-model="contestantImport.CreateMissingBibs"
                                    label="import.generatebibchip"
                                    tooltip="import.generatebibchiphelp"
                                />

                                <InputBool
                                    class="col-12 col-md-4 col-sm-6"
                                    v-model="contestantImport.GenerateTeams"
                                    label="import.generateteams"
                                    tooltip="import.generateteamshelp"
                                />
                                <InputBool
                                    class="col-12 col-md-4 col-sm-6"
                                    v-model="contestantImport.SameParticipantForMulitpleRaces"
                                    label="import.usesameparticipant"
                                    tooltip="import.useparticipanthelp"
                                />
                                <InputBool
                                    class="col-12 col-md-4 col-sm-6"
                                    v-model="contestantImport.SkipExistingContestants"
                                    label="import.skipexistingcontestants"
                                    tooltip="import.skipexistingcontestantshelp"
                                />
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <AlertMessage
                v-if="!contestantImport.RelayTeamsOnly && missingRequiredFields != null && missingRequiredFields.length > 0"
                type="warning"
            >
                {{ $tm('import.missingrequiredfields') }}
                <span class="fw-semibold fst-italic text-lowercase">
                    <span
                        v-for="(field, index) in missingRequiredFields"
                        :key="index"
                    >
                        {{ $tm(field) }}{{ index < missingRequiredFields.length - 1 ? ', ' : '' }}
                    </span>
                </span>
            </AlertMessage>

            <AlertMessage
                v-else-if="missingRequiredRelayFields != null && missingRequiredRelayFields.length > 0"
                type="warning"
            >
                {{ $tm('import.missingrequiredrelayfields') }}
                <span class="fw-semibold fst-italic text-lowercase">
                    <span
                        v-for="(field, index) in missingRequiredRelayFields"
                        :key="index"
                    >
                        {{ $tm(field) }}{{ index < missingRequiredRelayFields.length - 1 ? ', ' : '' }}
                    </span>
                </span>
            </AlertMessage>

            <AlertMessage
                v-if="globalWarning != ''"
                type="warning"
                :icon="true"
                ><span v-html="$tm(globalWarning, '', true)"></span
            ></AlertMessage>

            <ImportComp
                ref="importComp"
                :columns="colData"
                :rows="rowData"
                :importOptions="options"
                :validators="contestantImportDefaultValidators"
                :lockHeader="contestantImportDefaultSettings?.LockHeader ?? false"
                :use-filter="contestantImportDefaultSettings?.ShowFilter ?? false"
                :column-captions="contestantImportDefaultSettings?.ColumnCaptions"
                :dont-auto-map="dontAutomapHeaders"
                :readonly="contestantImportDefaultSettings.ReadOnly ?? false"
                @rowchanged="_onRowChanged"
                @column-map-changed="_onColumnMapChanged"
                @has-errors="(gotError) => (importHasErrors = gotError)"
                @import-completed="onImportCompleted"
                @column-deleted="onColumnsDeleted"
            ></ImportComp>
        </template>

        <template
            #footer
            v-if="isImportRowsLoaded"
        >
            <div class="d-flex justify-content-end align-items-center gap-2">
                <span class="text-nowrap me-5">
                    <InputBool
                        class="col-12 col-md-4 col-sm-6"
                        v-model="contestantImport.IgnoreRowsWithErrors"
                        label="import.ignoreerrors"
                    />
                </span>
                <ButtonComp
                    buttonType="Secondary"
                    @click="_resetImport"
                    >{{ $tm('participantimport.cancelimport') }}</ButtonComp
                >
                <ButtonComp
                    buttonType="Primary"
                    :disabled="!isValidImport || importIsDone"
                    @click.capture="_importData"
                    >{{ $tm('import.importdata') }}</ButtonComp
                >
            </div>
        </template>
    </ContentComp>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed, toRaw } from 'vue';
import { ImportOption, ImportOptions, ContestantImportDetails, ImportColumnMap, type IImportValue } from '@/components/Import';
import RaceLayoutMapper from './RaceLayoutMapper.vue';
import {
    Dropzone,
    ContentComp,
    ImportComp,
    IconComp,
    TooltipHelp,
    ButtonComp,
    InputBool,
    AlertMessage,
    DropdownSelect,
    LoadingComp,
    DisplayMimeType,
    DisplayFilename,
} from '@/components';
import { useLoaders, useTaskLoader } from '@/composeables/loaders';
import {
    Category,
    ContestantImport,
    ContestantImportDefaultSettings,
    ContestantImportRow,
    Group,
    ImportContent,
    ImportFormat,
    Race,
    EventTeam,
    Eventinfo,
    ContestantBase,
    Country,
    ExternalEventDatasourceResponse,
    FileArchiveItem,
} from '@/objects/generated';
import { ContestantDao } from '@/dao';
import {
    useCommonStoreRef,
    useContestantStore,
    useContestantStoreRef,
    useEventCommonStore,
    useEventStoreRef,
    useLocaleStoreRef,
    useToastStore,
    useUserStore,
} from '@/stores';
import {
    DateValidator,
    DayValidator,
    EmailValidator,
    GenderValidator,
    MonthValidator,
    TimeValidator,
    YearValidator,
    NotEmptyValidator,
    type IValidator,
} from '../Import/Validators';
import { NormalizedValue } from './NormalizedValue';
import { $tm, $ti, $td } from '@/locize';
import {
    toIsoStringWithoutTimezone,
    secondsToJsDate,
    tryParseDatestring,
    formatDateDefault,
    parseDateStringISO,
    tryParseTimestring,
    formatDateIso,
    addTimeToDate,
} from '@/utilities/dateutils';
import { watch } from 'vue';
import { type ComponentExposed } from 'vue-component-type-helpers';
import { randomstring } from '@/utilities/utils';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';
import { useDialog } from '@/composeables';
import ContestantImportDetailsView from './ContestantImportDetailsView.vue';
import { ContestantImportSettings } from '@/composeables/settings';
import { useStoredSettings } from '@/composeables/settings/storedSettings';
import { ContestantImportExtended } from './ContestantImportExtended';
import { type SelectLine } from '@/objects';
import NewImportFormat from './NewImportFormat.vue';
import MissingRacesMessage from '../Alert/MissingRacesMessage.vue';
import ContestantCopyFromEvent from '../views/Contestants/ContestantCopyFromEvent.vue';
import { watchDebounced } from '@vueuse/core';
import CountryMapper from './CountryMapper.vue';
import { getEventAgeForCurrentEvent } from '@/utilities/categoryutils';

// The different type of import sources we can have
// file: We are importing from a file
// event: We are importing from another event
// none: We are not importing anything
type Importsource = 'file' | 'event' | 'external' | 'none';

// What type do we want to import
const currentImportSource = ref<Importsource>('none');

// This is the last exception we got from the import
const currentException = ref<string | null>(null);

// The ref of the dropzone component
const dropzone = ref<InstanceType<typeof Dropzone> | null>(null);

const { AddLoader, LoadState, ClearLoaders } = useTaskLoader();

const { AddLoader: AddImportLoader, LoadState: ImportLoadState, ClearLoaders: ClearImportLoaders } = useTaskLoader();
useLoaders(AddImportLoader, ['CATEGORIES', 'RACES', 'GROUPS', 'EVENTTEAMS', 'STATIONROLES', 'CONTESTANTS', 'COUNTRIES']);

// The loaders for the import job
const { AddLoader: AddLoaderImport, LoadState: LoadStateImport, ClearLoaders: ClearLoadersImport } = useTaskLoader();
const showImportModal = ref(false);

// The contestant store
const contestantStore = useContestantStore();

// The reactive properties of the contestantstore
const { Contestants } = useContestantStoreRef();

const { Countries } = useCommonStoreRef();

const contentComp = ref<ComponentExposed<typeof ContentComp> | null>(null);

// Contains the currently selected format
const selectedFormat = ref<string | null>(null);

// The storedSettings object
const contestantImportSettings = ref<ContestantImportSettings | null>(null);

// If we want to automap the headers or not. By default we want to automap the headers
const dontAutomapHeaders = ref<boolean>(false);

/**
 * Contains a list of all the files that have been loaded
 * This can be multiple in case it is an event running over multiple days
 */
const externalEventData = ref<ExternalEventDatasourceResponse | null>(null);

/**
 * Get the common data we need to use to determine where the import will place each row
 */
const { eventID } = useEventStoreRef();
const comStore = useEventCommonStore();
const { user } = useUserStore();

// define refs
const selectedFiles = ref<File[] | null>(null);
const rowData = ref<any[]>([]);
const colData = ref<any[]>([]);

// This is a list of the rows that has been filterd out.
const filteredRowData: any[] = [];

// Handle to the import component
const importComp = ref<ComponentExposed<typeof ImportComp> | null>(null);

const { locale } = useLocaleStoreRef();

const sourceEvent = ref<Eventinfo | null>(null);

// Define the participant import object with a unique GUID and the eventID
const contestantImport = ref<ContestantImport>(ContestantImport.FromJsValues({}));
contestantImport.value.GUID = randomstring('import-', '-contestant');
contestantImport.value.EventID = eventID.value;
contestantImport.value.Locale = locale.value.Locale;

// Simple lookup to quickly find the correct autogenerated name for a category based on gender
const genderCategoryNames = {
    m: $tm('import.gendercategorymale'),
    f: $tm('import.gendercategoryfemale'),
    n: $tm('import.gendercategoryneutral'),
};

// Set up a default Setting if no other is provided
let defaultSetting = ContestantImportDefaultSettings.FromJsValues({});
defaultSetting.ShowToolbar = true;

const contestantImportDefaultSettings = ref<ContestantImportDefaultSettings>(defaultSetting);
const contestantImportDefaultValidators = ref<IValidator[] | null>(null);

const genderMaleValue = ref<string>(contestantImportDefaultSettings.value.GenderMale ?? '');
const genderFemaleValue = ref<string>(contestantImportDefaultSettings.value.GenderFemale ?? '');
const genderNeutralValue = ref<string>(contestantImportDefaultSettings.value.GenderNeutral ?? '');
const dateFormatValue = ref<string>(contestantImportDefaultSettings.value.DateFormat ?? '');
const timeFormatValue = ref<string>(contestantImportDefaultSettings.value.TimeFormat ?? '');

const loadingCompMessage = ref<string>('');
const loadingCompFiles = ref<FileArchiveItem[]>([]);

/**
 * When the import loader is closed we reset the message and files and hide the modal
 */
const closeImportLoadingComp = () => {
    loadingCompMessage.value = '';
    loadingCompFiles.value = [];
    showImportModal.value = false;
};

/**
 * Whenever the import has been started we will subscribe to the signalr event that will give us the status of the import
 */
signalrSubscribe.ContestantImportRowStatus({ ImportGuid: contestantImport.value.GUID }, (status) => {
    console.log('got status update', status);

    let statusText = status[0].Status ?? '';
    let totalRows = status[0].TotalRows;
    let currentRows = status[0].CurrentRow;
    let statusValues = { currentRow: currentRows, totalRows: totalRows, elapsedTime: '', timeRemaining: '', failedRows: 0 };
    const failedFile = status[0].FailedRowsFile ?? null;
    // If we dont have a statusText there is nothing to show.
    if (statusText == '') return;

    // is running
    if (currentRows > 0 && totalRows > 0) {
        if (totalRows == currentRows) {
            // done
            statusValues.elapsedTime = $td(secondsToJsDate(status[0].ElapsedTime! / 1000), 'TIME');
            statusValues.failedRows = status[0].FailedRows!;
            statusValues.totalRows = totalRows - status[0].FailedRows!;
        } else {
            // importing
            let diffRows = totalRows - currentRows;
            let interval = diffRows > 1000 ? 100 : diffRows > 100 ? 10 : 1;

            statusValues.currentRow = Math.floor(currentRows / interval) * interval;
            statusValues.timeRemaining = $td(secondsToJsDate((status[0].AverageTimePrRow! * (totalRows - currentRows)) / 1000), 'TIME');
        }
    }
    loadingCompMessage.value = $ti(status[0].Status!, statusValues, true);

    if (failedFile != null) {
        loadingCompFiles.value = [failedFile];
    }
});

// The column map is a map of the columns and the values (database fields) they are mapped to
const columnMap = ref<Map<string, string>>(new Map<string, string>());

// This is listening to the importComp and will tell us if there are any errors in the import. We use this to see if we are allowed to import the data or not.
const importHasErrors = ref(false);

// We use this one to determine if the import is done or not
const importIsDone = ref(false);

// This is a list of the required fields that are missing in the import
const missingRequiredFields = ref<string[] | null>(null);

// Contains the missing required fields for a relay import. For  Relay import we need to make sure that the Category field is present IF the import contains the RelayTeamName field
const missingRequiredRelayFields = ref<string[]>([]);

// This is a global warning that will be shown if there is any message that doesnt fit in the normal error handling
// This will make sure we cant import the data until the warning is resolved
const globalWarning = ref<string>('');

// Helper to determine if we have a header in the file or not

// This is a computed property that will tell us if we are allowed to import the data or not.
const isValidImport = computed(() => {
    // We need to have columns mapped, no required fields missing and no errors, or we have set the ignoreErrors flag in the import
    if (contestantImport.value.RelayTeamsOnly) {
        return (
            rowData.value.length > 0 &&
            missingRequiredRelayFields.value != null &&
            missingRequiredRelayFields.value.length == 0 &&
            globalWarning.value == '' &&
            (!importHasErrors.value || contestantImport.value.IgnoreRowsWithErrors)
        );
    } else {
        return (
            rowData.value.length > 0 &&
            missingRequiredFields.value != null &&
            missingRequiredFields.value.length == 0 &&
            missingRequiredRelayFields.value.length == 0 &&
            globalWarning.value == '' &&
            (!importHasErrors.value || contestantImport.value.IgnoreRowsWithErrors)
        );
    }
});

/**
 * We need to revalidate the data whenever certain fields have been changed.
 * This will update the validators with the changes and force a revalidation of the data
 * @param e
 * @param type
 */
const _revalidate = (type: 'Date' | 'Time' | 'Gender') => {
    let validator: IValidator | null = null;

    switch (type) {
        case 'Date':
            validator = new DateValidator(dateFormat());
            break;
        case 'Time':
            validator = new TimeValidator(timeFormat());
            break;
        case 'Gender':
            validator = new GenderValidator([...genderMale(), ...genderFemale(), ...genderNeutral()]);
            break;
    }

    if (validator != null) {
        importComp.value?.UpdateValidators(validator);
    }

    _updateAllRowDetails();
};

/**
 * The emits for the component
 */
const emits = defineEmits<{
    /**
     * When the import is completed successfully
     */
    (e: 'importcompleted'): void;
}>();

/**
 * Force to change validator and revalidate for all types
 */
const revalidateAll = () => {
    importComp.value?.UpdateValidators(new DateValidator(dateFormat()));
    importComp.value?.UpdateValidators(new TimeValidator(timeFormat()));
    importComp.value?.UpdateValidators(new GenderValidator([...genderMale(), ...genderFemale(), ...genderNeutral()]));

    _updateAllRowDetails();
};

/**
 * Will reset/cancel the importgrid and show the upload file again
 */
const _resetImport = () => {
    ClearLoaders();

    // We need to reuse the GUID or else we need to regenerate one and resubscribe
    const guid = contestantImport.value.GUID;

    currentImportSource.value = 'none';
    rowData.value = [];
    colData.value = [];

    currentImportFormat = null;
    currentException.value = null;
    isImportRowsLoaded.value = null;

    selectedFiles.value = null;
    columnMap.value = new Map<string, string>();
    contestantImportSettings.value = null;

    contestantImport.value = ContestantImport.FromJsValues({
        EventID: eventID.value,
        GUID: guid,
        Locale: locale.value.Locale,
    });

    dontAutomapHeaders.value = false;

    sourceEvent.value = null;
    importIsDone.value = false;

    quickCountryLookup = new Map<string, Country | null>();
    unknownCountries.value = new Set<string>();
};

/**
 * Helper function to normalize the gender values
 * @param gender
 */
const _genderMapper = (gender: string) =>
    gender
        .replace(/,/g, ' ')
        .split(' ')
        .map((x) => x.trim().toUpperCase());

/**
 * This contains the current values that are used to determine the male gender for the import
 * @returns string[] All the allowed values for a male
 */
const genderMale = () =>
    _genderMapper((genderMaleValue.value ?? '') == '' ? $tm('participantimport.defaultgendermale') : genderMaleValue.value);

/**
 * This contains the current values that are used to determine the female gender for the import
 * @returns string[] All the allowed values for female
 */
const genderFemale = () => {
    return _genderMapper((genderFemaleValue.value ?? '') == '' ? $tm('participantimport.defaultgenderfemale') : genderFemaleValue.value);
};

const genderNeutral = () => {
    return _genderMapper((genderNeutralValue.value ?? '') == '' ? $tm('participantimport.defaultgenderneutral') : genderNeutralValue.value);
};

/**
 * This contains the current date format that is used for the import
 * @returns string The date format
 */
const dateFormat = () => ((dateFormatValue.value ?? '') == '' ? $tm('participantimport.defaultdateformat') : dateFormatValue.value);

/**
 * This contains the current time format that is used for the import
 * @returns string The time format
 */
const timeFormat = () => ((timeFormatValue.value ?? '') == '' ? $tm('participantimport.defaulttimeformat') : timeFormatValue.value);

// If the filename we are uploading is joined
let formatFromFilename: ContestantImportExtended | null = null;

/**
 * Whenever the file list has changed
 * @param files
 */
const _onFileListChanged = (files: File[]) => {
    selectedFiles.value = files;

    // Check if we have a format with the same name as the first file, that means it will be preselected and we dont run the automap
    formatFromFilename =
        contestantImportSettings.value?.Formats?.find((x) => x.KnownFilenames?.includes(files[0].name?.toLowerCase())) ?? null;

    dontAutomapHeaders.value = formatFromFilename != null;
    _uploadFile();
};

const isImportRowsLoaded = ref<boolean | null>(null);

// We need to keep track of the current import format in case we want to do some post processing
let currentImportFormat: ImportFormat | null = null;

/**
 * If we have provided a ImportMap and a source event we will map the data from the source event to the current event
 */
const importfromsource = (content: ImportContent) => {
    colData.value = content.Columns;
    currentImportFormat = content.Format;

    // Set the default advanced settings
    contestantImport.value.GenerateCategories = content.Settings?.GenerateCategories ?? false;
    contestantImport.value.GenerateGroups = content.Settings?.GenerateGroups ?? false;
    contestantImport.value.SkipExistingContestants = content.Settings?.SkipExistingContestants ?? false;

    if (content.Settings != null) {
        contestantImportDefaultSettings.value = content.Settings;
        let _validators = [];
        if (content.Settings.DateFormat != null) {
            _validators.push(new DateValidator(content.Settings.DateFormat!));
        }

        if (content.Settings.TimeFormat != null) {
            _validators.push(new TimeValidator(content.Settings.TimeFormat!));
        }
        if (content.Settings.GenderMale != null && content.Settings.GenderFemale != null) {
            let genders = [content.Settings.GenderMale, content.Settings.GenderFemale];
            if (content.Settings.GenderNeutral != null) {
                genders.push(content.Settings.GenderNeutral);
            }
            _validators.push(new GenderValidator(genders));
        }
        contestantImportDefaultValidators.value = _validators;
    }

    isImportRowsLoaded.value = true;
    setTimeout(() => {
        rowData.value = content.Rows;
    }, 100);
};

/**
 * Will upload the file to the server and get the columns and rows back
 */
const _uploadFile = async () => {
    AddLoader(async () => {
        // TODO: We should probably add a check to make sure we only upload one file at a time
        if (selectedFiles.value == null || selectedFiles.value.length == 0) {
            throw new Error('No files selected');
        }

        // Create a FormData object to store the form data
        const formData = new FormData();

        // Append each selected file to the FormData object
        for (let i = 0; i < selectedFiles.value.length; i++) {
            formData.append('files', selectedFiles.value[i]);
        }

        let content: ImportContent[] | null = null;
        try {
            content = await ContestantDao.UploadFiles(formData, contestantImport.value.EventID);
        } catch (e) {
            console.log(e);
            dropzone.value?.reset(); // Reset the filelist
            currentException.value = 'contestantimport.invalidfile';
            return;
        }
        isImportRowsLoaded.value = false;
        let importContent: ImportContent | null = null;

        if (content.length == 1 && content[0].ErrorCode == 0) {
            importContent = content[0];
        } else {
            // TODO: Show error
            return;
        }

        // Handle the import error
        if (importContent == null || importContent.ErrorCode != 0) {
            // TODO: Handle error
        }

        currentImportFormat = importContent.Format;

        colData.value = content[0].Columns;

        // Handle settings

        contestantImportDefaultSettings.value = importContent.Settings ?? defaultSetting;

        if (importContent.Settings != null) {
            contestantImportDefaultSettings.value = importContent.Settings;
            let _validators = [];
            if (importContent.Settings.DateFormat != null) {
                _validators.push(new DateValidator(importContent.Settings.DateFormat!));
            }

            if (importContent.Settings.TimeFormat != null) {
                _validators.push(new TimeValidator(importContent.Settings.TimeFormat!));
            }
            if (importContent.Settings.GenderMale != null && importContent.Settings.GenderFemale != null) {
                let genders = [importContent.Settings.GenderMale, importContent.Settings.GenderFemale];

                if (importContent.Settings.GenderNeutral != null) {
                    genders.push(importContent.Settings.GenderNeutral);
                }

                _validators.push(new GenderValidator(genders));
            }
            contestantImportDefaultValidators.value = _validators;
        }

        isImportRowsLoaded.value = true;
        setTimeout(() => {
            rowData.value = content[0].Rows;
            _updateAllRowDetails();
        }, 100);
    }, '');
};

// This is the callback function when the data has been validated in the grid and ready to be sendt to the server
const _importData = () => {
    const valuemap = importComp.value?.GetValuemap<ContestantImportRow>(ContestantImportRow) || null;

    if (valuemap == null || valuemap.Rows.length == 0 || valuemap.Cols.length == 0) {
        return;
    }

    ClearLoaders();
    ClearImportLoaders();

    contestantImport.value.Cols = valuemap.Cols;
    contestantImport.value.Rows = valuemap.Rows;
    contestantImport.value.Format = !currentImportFormat ? ImportFormat.FromJsValues({}) : ImportFormat.FromOther(currentImportFormat);

    let details: ContestantImportDetails[] = [];
    rowData.value.forEach((row) => {
        // We only need the details for the rows that are in the valuemap
        if (valuemap.Rows.find((x) => x.ID == parseInt(row.id)) != null) {
            details.push(row.details);
        }
    });
    let newformat: { name: string; format?: ImportFormat } = { name: '', format: contestantImport.value.Format };
    useDialog().YesNo({
        title: 'general.details',
        body: ContestantImportDetailsView,

        yesCallback: async () => {
            showImportModal.value = true;
            // TODO: We should probably move most of this handling to the ImportComp.
            AddLoaderImport(async () => {
                const content = await ContestantDao.ImportContestants(contestantImport.value, contestantImport.value.EventID);
                if (content == null) {
                    throw new Error('No response from server');
                }
                // Find all rows with DBErrors != null and set the error on the row
                content.Rows.forEach((row) => {
                    if (row.DBErrors != null && row.DBErrors.length > 0) {
                        let rowId = row.ID;
                        let srcRow = rowData.value.find((x) => x.id == rowId);
                        if (srcRow != null) {
                            importComp.value?.SetRowError(srcRow, row.DBErrors);
                        }
                    }
                });
                importComp.value?.ImportData();

                saveAndSelectNewFormat(newformat);

                // confirmModalClose.value = false;
            }, '');

            return true;
        },
        componentProps: {
            value: details,
            validRows: valuemap.Rows.length,
            invalidRows: 0,
            importformat: selectedFormat.value == null ? newformat : undefined,
        },
        options: {
            Size: 'lg',
        },
    });
};

/**
 * Will maintain a map of the columns and the values (database fields) they are mapped to
 * If we are using automap i.e. we have a header in the file, then this one will be fired first time only when all the columns are mapped instead of one by one.
 * @param map: ImportColumnMap[] this is the map of the columns and the values (database fields) they are mapped to that have been changed.
 */
const _onColumnMapChanged = (map: ImportColumnMap[]) => {
    let updateableColumns = [
        'Gender',
        'Category',
        'Birthday',
        'Year',
        'Group',
        'Firstname',
        'Surname',
        'FullnameLastFirst',
        'FullnameFirstFirst',
        'Startnumber',
        'Nationality',
        'EventTeamName',
        'RelayTeamName',
        'Starttime',
    ];

    saveStoredSettings();

    let needUpdate = false;
    let _map = new Map<string, string>();
    map.forEach((x) => {
        let oldValue = columnMap.value.get(x.column) || '';

        // columnMap.value.set(x.column, x.value);
        _map.set(x.column, x.value);

        if (!needUpdate) {
            needUpdate = updateableColumns.includes(oldValue) || updateableColumns.includes(x.value);
        }
    });

    // Concat the two maps
    columnMap.value = new Map([...columnMap.value, ..._map]);

    // Sets if we are doing a result import or not
    isResultImport.value =
        _getColumnMapfield('Rank') != null || _getColumnMapfield('ResultTime') != null || _getColumnMapfield('ResultPoints') != null;

    if (needUpdate && _checkRequiredFields()) {
        _updateAllRowDetails();
    }
};

/**
 * When we have deleted a column we need to remove it from the columnMap
 * TODO: This is not implemented in the header yet. We need to add a delete button to the header
 * @param colid
 */
const onColumnsDeleted = (colid: string) => {
    columnMap.value.delete(colid);

    // TODO: Do we care about removing the actual data from the rows? it will save us some memory but it is only temporary until we finish the import
};

/**
 * Is this a resultimport or not?
 */
const isResultImport = ref<boolean>(false);

/**
 * Force an update of the details on all rows
 */
const _updateAllRowDetails = () => {
    // Clear the countries
    unknownCountries.value.clear();

    // We have missing required fields, no point in trying to normalize the data
    if (!contestantImport.value?.RelayTeamsOnly && (missingRequiredFields.value ?? []).length > 0) {
        return;
    } else if (missingRequiredRelayFields.value.length > 0) {
        return;
    }

    importComp.value?.ClearAllStatuses();

    // We create a set of all existing ParticipantIds
    let existingParticipantIds = new Set<number>(Contestants.value.map((x) => x.ParticipantID));

    let _rows = toRaw(rowData.value);

    _rows = _rows.concat(filteredRowData);

    filteredRowData.length = 0;
    // Filter the rows.
    _rows = _rows.filter((row) => {
        let sourceParticipantId = !row['@SourceParticipantId'] ? null : (row['@SourceParticipantId'] as number);
        // If we
        if (
            sourceParticipantId != null &&
            contestantImport.value.SkipExistingContestants &&
            existingParticipantIds.has(sourceParticipantId)
        ) {
            filteredRowData.push(row);
            return false;
        }

        _updateRowDetails(row);
        return true;
    });

    rowData.value = _rows;
    importComp.value?.RefreshRows();
};

/**
 * Triggered whenever a row has been changed in the grid
 * @param row
 */
const _onRowChanged = (row: any) => {
    _updateRowDetails(row);

    // We need to update the row in the grid
    // TODO: Here maybe we can find a way to just refresh the row instead of the whole grid
    importComp.value?.RefreshRows();
};

/**
 * Helper function to find a value in the map
 * @param field
 */
const _getColumnMapfield = (field: string) => {
    for (const [key, value] of columnMap.value.entries()) {
        if (value === field) return key;
    }
    return undefined;
};

/**
 * Helper function to normalize the name based on the column settings
 * @param row
 * @returns normalized name
 */
const _normalizeName = (row: any) => {
    let firstnameColumn = _getColumnMapfield('Firstname');
    let surnameColumn = _getColumnMapfield('Surname');
    let fullnameLastFirstColumn = _getColumnMapfield('FullnameLastFirst');
    let fullnameFirstFirstColumn = _getColumnMapfield('FullnameFirstFirst');

    let normalizedName = '';

    // Check name
    if (firstnameColumn && surnameColumn) {
        normalizedName = row[firstnameColumn].Value + ' ' + row[surnameColumn].Value;
        if (row[surnameColumn].Value.trim() == '') {
            // Always need surname
            normalizedName = '';
        }
    } else if (fullnameLastFirstColumn) {
        const split = row[fullnameLastFirstColumn].Value.split(' ');
        let surname = split.length > 1 ? split[0] : '';
        let firstname = split.length > 1 ? split.slice(1).join(' ') : '';
        normalizedName = firstname + ' ' + surname;
    } else if (fullnameFirstFirstColumn) {
        normalizedName = row[fullnameFirstFirstColumn].Value;
    }

    return normalizedName;
};

/**
 * Helper function to normalize the birthday based on the column settings
 * @param row
 * @returns age and normalizedBirthday
 */
const _normalizeBirthdayAndAge = (row: any, isRelayTeamRow: boolean) => {
    let yearColumn = _getColumnMapfield('Year');
    let birthdayColumn = _getColumnMapfield('Birthday');

    let age = null;
    let normalizedBirthday = null;

    // Calculate age
    if (birthdayColumn) {
        let birthday = tryParseDatestring(row[birthdayColumn].Value, dateFormat()!);
        if (birthday != null) {
            age = new Date().getFullYear() - birthday.getFullYear();

            // Here we have to take in account the event year and the licence year
            const eventAge = getEventAgeForCurrentEvent(birthday);
            if (eventAge != null && eventAge != age) {
                age = eventAge;
            }

            // Format birtday to yyyy-mm-ddd
            normalizedBirthday = toIsoStringWithoutTimezone(birthday).split('T')[0];
        }
    } else if (yearColumn && !isNaN(parseInt(row[yearColumn].Value))) {
        let year = parseInt(row[yearColumn].Value);

        let currentYear = new Date().getFullYear();
        let currentYear2digit = currentYear - 2000;
        if (year < 1899) {
            // Normalize two digit years to four digit years, we assume no one is older than 100 years.
            year += year >= 0 && year <= currentYear2digit ? 2000 : 1900;
        }

        let monthColumn = _getColumnMapfield('Month');
        let dayColumn = _getColumnMapfield('Day');

        let month = monthColumn ? parseInt(row[monthColumn].Value) : 1;
        let day = dayColumn ? parseInt(row[dayColumn].Value) : 1;

        age = currentYear - year;
        normalizedBirthday = toIsoStringWithoutTimezone(new Date(year, month - 1, day)).split('T')[0];
    }

    if (isRelayTeamRow && age == null && normalizedBirthday == null) {
        const relayTeamDate = new Date(1899, 11, 31);

        age = new Date().getFullYear() - relayTeamDate.getFullYear();
        normalizedBirthday = toIsoStringWithoutTimezone(relayTeamDate).split('T')[0];
    }

    return { age, normalizedBirthday };
};

/**
 * Normalize the Gender based on the column settings
 * @param row
 * @return gender and genderNormalized
 */
const _normalizeGender = (row: any, isRelayTeamRow: boolean) => {
    let genderColumn = _getColumnMapfield('Gender');

    let gender = '';
    let genderNormalized = null;
    if (genderColumn) {
        if (genderMale().includes(row[genderColumn].Value.toUpperCase())) {
            gender = 'm';
            genderNormalized = 'm'; // this should probably be the enum value
        } else if (genderFemale().includes(row[genderColumn].Value.toUpperCase())) {
            gender = 'f';
            genderNormalized = 'f'; // This should probably be the enum value
        } else if (genderNeutral().includes(row[genderColumn].Value.toUpperCase())) {
            gender = 'n';
            genderNormalized = 'n'; // This should probably be the enum value
        }
    }

    /**
     * We default the gender to male if it is not provided and is a relay team row, this could MAYBE be neutral
     */
    if (isRelayTeamRow && gender == '' && genderNormalized == null) {
        gender = 'm';
        genderNormalized = 'm'; // This should probably be the enum value
    }

    return { gender, genderNormalized };
};

/**
 * We try and parse the ResultTime into a new Date
 * @param row
 */
const _normalizeResultTime = (row: any): string | null => {
    let resultTimeColumn = _getColumnMapfield('ResultTime');
    if (resultTimeColumn) {
        let resultTime = _normalizeTime(row[resultTimeColumn].Value);
        if (resultTime != null) {
            return formatDateIso(resultTime);
        }
    }
    return null;
};

/**
 * We try and normalize the startingtime column
 * @param row
 */
const _normalizeStarttime = (row: any): string | null => {
    let startTimeColumn = _getColumnMapfield('Starttime');
    if (startTimeColumn) {
        let startTime = _normalizeTime(row[startTimeColumn].Value);
        if (startTime != null) {
            // We need to normalize the starttime to event start if date = 2001-01-01
            if (startTime.getFullYear() == 2001 && startTime.getMonth() == 0 && startTime.getDate() == 1) {
                startTime = addTimeToDate(startTime, comStore.Event?.Date ?? new Date());
            }

            if (startTime == null) {
                return null;
            }

            return formatDateIso(startTime);
        }
    }
    return null;
};

/**
 * Normalize the a time value
 * @param value
 */
const _normalizeTime = (value: string): Date | null => tryParseTimestring(value, timeFormat()!) ?? null;

/**
 * Try and find a correct class based on the gender and age
 * @param row
 * @param age
 * @param gender
 * TODO: We should also use the NormalizeType function to find the correct class
 */
const _normalizeCategory = (row: any, age: number | null, gender: string): NormalizedValue<Category> => {
    let categoryColumn = _getColumnMapfield('Category');
    let category: string | null = null;
    let generated = false;
    let exists = false;
    let defaultGenerated = false;

    // const fnExists = (x: string) => comStore.Categories.find((y) => y.Name?.toLocaleLowerCase() == x.toLowerCase()) != null;
    // const fnFind = (age: number, gender: string) =>
    //     comStore.Categories.find((x) => x.AgeFrom! <= age && x.AgeTo! >= age && (x.Gender == 'n' || x.Gender == gender))?.Name || null;

    let src: Category | null = null;
    if (categoryColumn) {
        category = (row[categoryColumn].Value || '') == '' ? null : row[categoryColumn].Value;

        // Check if exists
        src = comStore.Categories.find((y) => y.Name?.toLocaleLowerCase() == category?.toLowerCase()) ?? null;
        exists = category != null && src != null;
    } else if (age != null) {
        // We try and map the category from Age and Gender
        // we try first and find by gender, then by both
        src = comStore.Categories.find((x) => x.AgeFrom! <= age && x.AgeTo! >= age && x.Gender == gender) ?? null;

        if (!src && gender != 'n') {
            src = comStore.Categories.find((x) => x.AgeFrom! <= age && x.AgeTo! >= age && x.Gender == 'n') ?? null;
        }

        src = comStore.Categories.find((x) => x.AgeFrom! <= age && x.AgeTo! >= age && (x.Gender == 'n' || x.Gender == gender)) ?? null;
        category = src?.Name ?? null;
        exists = category != null;
    }

    if (contestantImport.value.GenerateCategories && (category == null || (category != null && !exists))) {
        // We have no category and we are allowed to generate categories
        defaultGenerated = !(!exists && category != null);
        generated = true;
        category =
            !exists && category != null
                ? category
                : gender == 'm'
                  ? genderCategoryNames.m
                  : gender == 'f'
                    ? genderCategoryNames.f
                    : genderCategoryNames.n;
    } else if (!contestantImport.value.GenerateCategories && category == null) {
        // We have no category and we are not allowed to generate categories but we still need a category so we create the default localized gender categories
        defaultGenerated = !(!exists && category != null);
        generated = true;
        category =
            !exists && category != null
                ? category
                : gender == 'm'
                  ? genderCategoryNames.m
                  : gender == 'f'
                    ? genderCategoryNames.f
                    : genderCategoryNames.n;
    }

    let val = new NormalizedValue<Category>(category, exists, generated);
    val.src = src;
    val.defaultGenerated = defaultGenerated;

    return val;
};

let quickCountryLookup = new Map<string, Country | null>();

// Contains the list of unknown values that we need to map with the country mapper
const unknownCountries = ref<Set<string>>(new Set<string>());

// Contains the list of countries that we have mapped
const countryMappingValues = ref<Map<string, Country> | null>(new Map<string, Country>());

/**
 * Will find the correct country based on the row.
 * return null if there is no country
 * @param row
 */
const _normalizeNationality = (row: any): NormalizedValue<Country> | null => {
    let nationalityColumn = _getColumnMapfield('Nationality');

    if (!nationalityColumn) {
        return null;
    }

    let value = row[nationalityColumn].Value;
    let valueLowercase = value?.toLowerCase();
    let valueUppercase = value?.toUpperCase();

    // Check if we have a quick lookup for the country
    let country: Country | null = null;
    country = quickCountryLookup.get(valueLowercase) ?? null;

    if (country != null) {
        return new NormalizedValue<Country>(value, true, false, country.ID);
    }

    // Check if there is anything in the CountryMapper
    country = countryMappingValues.value?.get(valueUppercase) ?? null;

    if (country != null) {
        quickCountryLookup.set(value, country);
        return new NormalizedValue<Country>(value, true, true, country.ID);
    }

    // We check if the value is in the complete country list. This is the slowest lookup by far so we do this last
    if (country == null) {
        country = Countries.value.find((x) => x.ISO2 == valueUppercase || x.Name?.toLocaleLowerCase() == valueLowercase) ?? null;

        if (country != null) {
            quickCountryLookup.set(value, country);
            return new NormalizedValue<Country>(value, true, false, country.ID);
        }
    }

    return null;
};

/**
 * Will find out if we need to add a race, and if it allready exist or will be added
 * @param row
 * TODO: Maybe we want a possibility to send the race name in the import file? I dont know if that is a good idea or not.
 */
let defaultRaceForEvent: NormalizedValue<Race> | null = null;
const _normalizeRace = (row: any) => {
    if (contestantImport.value.GenerateRacesForGroups) {
        // we want to create a new race for each unique group so that means each row need a group or else we will not be able to know what race it goes to
        // if we allready have a grou we know that the name will be the same as the group
        let normalizedValue = _normalizeGroup(row);
        if (normalizedValue?.value == null) {
            return null;
        }

        // We check if the race with that name allready exists
        normalizedValue.exists =
            comStore.Races.find((y) => y.Name?.trim() != '' && y.Name?.toLowerCase() == normalizedValue.value?.toLowerCase()) != null;

        // We see if we need to generate it or not. If it allready exists we dont need to generate it
        normalizedValue.generated = !normalizedValue.exists;

        return normalizedValue;
    } else if (defaultRaceForEvent != null) {
        // If we only have one race and it has allready been determined, we will use that one
        return defaultRaceForEvent;
    } else {
        // check if there is atleast one race or else we create one based on the locale.
        let race = comStore.Races.find((y) => y.Name?.trim() != '');

        let normalized = new NormalizedValue<Race>(null, false, false);
        normalized.value = race == null ? $tm('import.defaultracename') : race.Name;
        normalized.id = race == null ? undefined : race.ID;
        normalized.exists = race != null;
        normalized.generated = race == null;
        normalized.src = race ?? null;

        defaultRaceForEvent = normalized;

        return defaultRaceForEvent;
    }
};

/**
 * Normalize groups and check if we need to generate a new group or if it allready exists
 * @param row
 */
const _normalizeGroup = (row: any) => {
    return _normalizeType<Group>(row, comStore.Groups, 'Group', contestantImport.value.GenerateGroups);
};

/**
 * Normalize teams and check if we need to generate a new team or if it allready exists
 * @param row
 */
const _normalizeTeam = (row: any) =>
    _normalizeType<EventTeam>(row, comStore.EventTeams, 'EventTeamName', contestantImport.value.GenerateTeams);

interface INormalizedContestant {
    contestant: ContestantBase | null;
    age: number | null;
    birthdayNormalized: string | null;
    genderNormalized: 'm' | 'f' | 'n' | null;
    gender: 'm' | 'f' | 'n' | null;
    nameNormalized: string | null;
    sourceParticipantId: number | null;
}

/**
 * We get the most important details for the contestant based on existing data
 * @param row
 * @param importDetails
 */
const getExistingContestant = (row: any) => {
    let data: INormalizedContestant = {
        contestant: null,
        age: null,
        birthdayNormalized: null,
        genderNormalized: null,
        gender: null,
        nameNormalized: null,
        sourceParticipantId: null,
    } as INormalizedContestant;

    let bibColumn = _getColumnMapfield('Startnumber');

    let sourceParticipantId: number | null = row['@SourceParticipantId'] ?? null;

    if (bibColumn == null && sourceParticipantId == null) {
        return data;
    }

    if (sourceParticipantId != null) {
        data.contestant = Contestants.value.find((x) => x.ParticipantID == sourceParticipantId) ?? null;
        data.sourceParticipantId = sourceParticipantId;
    } else {
        let startnumber = !bibColumn ? null : row[bibColumn].Value;
        data.contestant = Contestants.value.find((x) => x.Bib == startnumber) ?? null;
    }

    // TODO: Here we have to take in account the event year and the licence year
    if (data.contestant?.Birthday != null) {
        data.age = new Date().getFullYear() - data.contestant.Birthday.getFullYear();
    }

    data.nameNormalized = data.contestant == null ? null : `${data.contestant?.Firstname} ${data.contestant?.Surname}`.trim();
    data.birthdayNormalized =
        data.contestant == null || data.contestant.Birthday == null
            ? null
            : toIsoStringWithoutTimezone(data.contestant?.Birthday).split('T')[0];
    data.genderNormalized = data.contestant == null ? null : (data.contestant?.Gender ?? 'm');
    data.gender = data.contestant == null ? null : (data.contestant.Gender ?? 'm');

    // TODO: We should also map in the RaceContestantID and starttime if we have it

    return data;
};

/**
 * Internal function to normalize the values based on the columnfield and the comstore
 * @param row
 * @param comstore
 * @param columnfield
 * @param allowGeneratation
 */
const _normalizeType = <T extends { Name: string | null; ID: number | null }>(
    row: any,
    comstore: T[] | Map<number, T>,
    columnfield: string,
    allowGeneratation: boolean,
) => {
    let column = _getColumnMapfield(columnfield);
    let normalized = new NormalizedValue<T>(null, false, false);

    if (!column) {
        return null;
    }

    let value = row[column]?.Value || '';

    if (!value) {
        return normalized;
    }

    if (column) {
        let values: T[];
        if (comstore instanceof Map) {
            values = Array.from(comstore.values());
        } else {
            values = comstore;
        }
        const fnExists = (x: string) => values.find((y) => y.Name?.trim() != '' && y.Name?.toLowerCase() == x.toLowerCase());
        let src = fnExists(value);
        normalized.value = value;
        normalized.exists = src != null;
        normalized.src = src ?? null;
    }

    normalized.generated = allowGeneratation && (normalized.value == null || (normalized.value != null && !normalized.exists));

    return normalized;
};

/**
 * This is the main function that will analyse each row and determine what categories, groups, races and ages they belong to. This is also the
 * dataset we will use to determine what kind of Categories, groups etcs that will be automatically created or allocated.
 * @param row
 */
const _updateRowDetails = (row: any) => {
    if (importComp.value?.HasHeader() && row.id == '0') {
        return;
    }

    if (!contestantImport.value?.RelayTeamsOnly && (missingRequiredFields.value ?? []).length > 0) {
        return;
    } else if (missingRequiredRelayFields.value.length > 0) {
        return;
    }

    // TODO: We need to know the licence year and the event year to properly calculate the Age
    // Do we have a defined Øvelse?

    let categoryColumn = _getColumnMapfield('Category');
    let nationalityColumn = _getColumnMapfield('Nationality');

    let importDetails = new ContestantImportDetails();

    let isRelayTeamRow = false;
    let relayTeamCol = _getColumnMapfield('RelayTeamName');
    if (relayTeamCol) {
        isRelayTeamRow = row[relayTeamCol].Value?.trim() != '';
    }

    // Reset the normalized values
    // NOTE! The normalized values are the strong typed values based on the parsing rules for Gender, DateTime etc.
    row['@GenderNormalized'] = null;
    row['@BirthdayNormalized'] = null;
    row['@ResultTimeNormalized'] = null;
    row['@ContestantIdNormalized'] = null;
    row['@ResultTimeNormalized'] = null;
    row['@StarttimeNormalized'] = null;
    row['@NationalityNormalized'] = null;
    row['@NationalityNormalized'] = null;
    // row['@SourceClubId'] = null;

    // clear the row status
    importComp.value?.SetRowWarning(row, null);
    importComp.value?.SetRowError(row, null);

    let existingContestant = getExistingContestant(row);

    // Helper to see that we have an existing contestant.
    // If we allready have an exisitng contestant we will assume that it is correctly added to groups, categories etc.
    let hasExistingContestant = existingContestant.contestant != null;

    let normalizedBirthdayAndAge = _normalizeBirthdayAndAge(row, isRelayTeamRow);
    let normalizedGender = _normalizeGender(row, isRelayTeamRow);
    let normalizedResultTime = _normalizeResultTime(row);
    let normalizedStarttime = _normalizeStarttime(row);
    let normalizedNationality = _normalizeNationality(row);

    importDetails.Name = existingContestant.nameNormalized ?? _normalizeName(row);
    importDetails.Birthday = existingContestant.birthdayNormalized ?? normalizedBirthdayAndAge.normalizedBirthday;
    importDetails.Age = existingContestant.age ?? normalizedBirthdayAndAge.age;
    importDetails.Gender = existingContestant.gender ?? normalizedGender.gender;

    row['@BirthdayNormalized'] = importDetails.Birthday;
    row['@GenderNormalized'] = existingContestant.genderNormalized ?? normalizedGender.genderNormalized;
    row['@ResultTimeNormalized'] = normalizedResultTime;
    row['@StarttimeNormalized'] = normalizedStarttime;

    const nameHastFirstAndSurname = (importDetails.Name?.trim().split(' ').length ?? 0) >= 2;

    // We check if we have a nationaliyt column and then we check if there is any nationality we cannot map and then we add them in the unknowns country list.
    if (nationalityColumn && normalizedNationality == null) {
        unknownCountries.value.add(row[nationalityColumn].Value);
    }

    // If we have a nationality we will set the normalized value ( the ID of the country)
    if (normalizedNationality != null) {
        row['@NationalityNormalized'] = normalizedNationality.id!;
        importDetails.NationalityID = normalizedNationality.id!;
    }

    // We try and normalize the category based on the current settings.
    // Category is a required field, so we will always try and normalize it. it is either an actual category, or one based on gender and age
    let normalizedCategory = _normalizeCategory(row, importDetails.Age, importDetails.Gender);
    importDetails.Category = normalizedCategory.value;
    importDetails.CategoryGenerated = normalizedCategory.generated;

    // We try and normalize the group based on the current settings.
    // This will give us an object telling us what group are used and if it allready exists or if we need to generate a new one.
    let normalizedGroup = _normalizeGroup(row);
    if (normalizedGroup?.value != null) {
        importDetails.Group = normalizedGroup.value;
        importDetails.GroupGenerated = normalizedGroup.generated;
    }

    // We try and normalize the team based on the current settings.
    // This will give us an object telling us what team are used and if it allready exists or if we need to generate a new one.
    let normalizedTeam = _normalizeTeam(row);
    if (normalizedTeam?.value != null) {
        importDetails.Team = normalizedTeam.value;
        importDetails.TeamGenerated = normalizedTeam.generated;
    }

    // If there is an clubId in the mapping we add the source club id to the row
    let clubIdCol = _getColumnMapfield('_SourceClubID_');
    if (clubIdCol) {
        let clubId = parseInt(row[clubIdCol]?.Value || '');
        if (!isNaN(clubId)) {
            row['@SourceClubId'] = clubId;
        }
    }

    // We try and normalize the race based on the current settings.
    // This will give us an object telling us what race are used and if it allready exists or if we need to generate a new one.
    // There should always be a Race, eiter a default race (If there is allready races in the event, we will pick the first one)
    // if the setting GenerateRaceForGroups is set, we will generate a new Race for each unique group
    let normalizedRace = _normalizeRace(row);
    if (normalizedRace?.value != null) {
        importDetails.Race = normalizedRace.value;
        importDetails.RaceGenerated = normalizedRace.generated;
    }

    // Kun 1 pulje skal alltid velges dersom det bare finnes 1 og det ikke er satt column group
    // Dersom det er er flere puljer og klassen passer inn i KUN 1 pulje (PuljeKlasse) så skal denne puljen velges
    // Hvis ingen puljer kan velges så skal vi ha en feilmelding på raden
    // Velg øvelsen som passer til puljen.
    if (normalizedGroup == null || normalizedGroup.value == null) {
        let group: Group | null = comStore.Groups.length == 1 ? comStore.Groups[0] : null;
        if (group != null) {
            // If we only have one group, always selct that one
            normalizedGroup = new NormalizedValue(group.Name, true, false);
            importDetails.Group = normalizedGroup.value;
            importDetails.GroupGenerated = normalizedGroup.generated;
        } else if (normalizedCategory != null && normalizedCategory.src != null) {
            // We did not find any group and need to check via the category
            let groupConnections = normalizedCategory.src.CategoryGroupConnection?.filter(
                (x) => x.CategoryID == normalizedCategory.src?.ID,
            );

            if (groupConnections?.length == 1) {
                // We have only one group that fits the category
                group = comStore.Groups.find((x) => x.ID == groupConnections[0].GroupID) ?? null;
                if (group != null) {
                    normalizedGroup = new NormalizedValue(group.Name, true, false);
                    importDetails.Group = normalizedGroup.value;
                    importDetails.GroupGenerated = normalizedGroup.generated;
                }
            }
        }

        if (group != null) {
            // We need to get the race for the group
            let race = comStore.Races.find((x) => x.ID == group.RaceID) ?? null;
            if (race != null) {
                normalizedRace = new NormalizedValue<Race>(race.Name, true, false);
                normalizedRace.id = group.RaceID;
                importDetails.Race = normalizedRace.value;
                importDetails.RaceGenerated = normalizedRace.generated;
            }
        }
    }

    // If it is a resultimport we need to know what Race it is and that we have a Finish point.
    let finishStation = null;
    if (isResultImport.value) {
        if (normalizedRace != null) {
            // TODO: Should we auto create a finish station if it does not exist? or have an advanced setting for this?
            finishStation = comStore.StationRoles.find((x) => x.RaceID == normalizedRace.id && x.Finish);
        }
    }

    // Check if we have enough info to create a contestant if the contestant does not allready exist
    let canCreateContestant =
        hasExistingContestant || (missingRequiredFields.value?.length == 0 && missingRequiredRelayFields.value.length == 0);

    // Check if we have a starttime either on the existion contestant row, the group or the category
    let starttime =
        existingContestant?.contestant?.RaceContestants?.find((x) => x.StartingTime != null)?.StartingTime ??
        normalizedStarttime ??
        normalizedGroup?.src?.StartTime ??
        normalizedCategory?.src?.StartTime;

    // Add Errors to the row
    if (isResultImport.value && !hasExistingContestant && !canCreateContestant) {
        setRowDetailTextAndStatus(importDetails, row, $tm('import.missingcontestant'), 'error');
    } else if (isResultImport.value && finishStation == null) {
        // We want to create a result, but we dont have a finish station
        setRowDetailTextAndStatus(importDetails, row, $ti('import.missingfinishstation', { race: normalizedRace?.value }, true), 'error');
    } else if (isResultImport.value && starttime == null) {
        // We want to create a result, but we dont have a starttime
        setRowDetailTextAndStatus(importDetails, row, $tm('import.missingstarttime', '', true), 'error');
    } else if ((row['@BirthdayNormalized'] ?? null) == null) {
        // Invalid birthday
        setRowDetailTextAndStatus(importDetails, row, $tm('import.missingbirthdayoryear'), 'error');
    } else if ((row['@GenderNormalized'] ?? null) == null) {
        // Invalid gender
        setRowDetailTextAndStatus(importDetails, row, $tm('import.missinggender'), 'error');
    } else if (!canCreateContestant && normalizedGroup == null) {
        // If we dont have a group, we also cant join with a race so we need to show an error
        setRowDetailTextAndStatus(importDetails, row, $tm('import.unabletofindgrouprace'), 'error');
    } else if (
        !hasExistingContestant &&
        categoryColumn != null &&
        (importDetails.Category == null ||
            (normalizedCategory.value != null && !normalizedCategory.exists && !normalizedCategory.generated))
    ) {
        // Category doesnt exist
        setRowDetailTextAndStatus(importDetails, row, $tm('import.nomatchingcategory'), 'error');
    } else if (
        !hasExistingContestant &&
        normalizedGroup != null &&
        normalizedGroup.value != null &&
        !normalizedGroup.exists &&
        !normalizedGroup.generated
    ) {
        // Group doesnt exist
        setRowDetailTextAndStatus(importDetails, row, $tm('import.nomatchinggroup'), 'error');
    } else if (normalizedTeam != null && normalizedTeam.value != null && !normalizedTeam.exists && !normalizedTeam.generated) {
        // Team doesnt exist
        setRowDetailTextAndStatus(importDetails, row, $tm('import.nomatchingteam'), 'error');
    } else if (contestantImport.value.GenerateRacesForGroups && normalizedRace == null) {
        // We want to create a race for each group, but we dont have a group on this row
        setRowDetailTextAndStatus(importDetails, row, $tm('import.rowmissinggroupforrace'), 'error');
    } else if (nationalityColumn != null && normalizedNationality == null) {
        // We have a column for nationality but we cant find the country for the row
        setRowDetailTextAndStatus(importDetails, row, $tm('import.invalidcountry'), 'error');
    } else if (
        normalizedCategory.generated &&
        normalizedCategory.defaultGenerated &&
        categoryColumn != null &&
        row[categoryColumn].Value.trim() === ''
    ) {
        // Generated category
        setRowDetailTextAndStatus(importDetails, row, $tm('import.nopossiblecategory'), 'error');
    }
    // warning rows
    else {
        let hasWarning: IImportValue | null = null;
        for (const [key, value] of Object.entries<IImportValue>(row)) {
            if (key.startsWith('_col') && value.StatusType == 'warning') {
                hasWarning = value;
                break;
            }
        }

        if (hasWarning && hasWarning.Status != '') {
            // TODO: If there is a warning we should show it here
            setRowDetailTextAndStatus(importDetails, row, $tm(hasWarning.Status!, '', true), 'warning');
        }

        if (!nameHastFirstAndSurname) {
            let fullnameLastFirstColumn = _getColumnMapfield('FullnameLastFirst');
            let fullnameFirstFirstColumn = _getColumnMapfield('FullnameFirstFirst');
            if (fullnameLastFirstColumn != null) {
                // Name only has one part, but we have a fullname column, so we show a warning message about it
                setRowDetailTextAndStatus(importDetails, row, $tm('import.nameonlyhassurname', '', true), 'warning');
            } else if (fullnameFirstFirstColumn != null || fullnameLastFirstColumn != null) {
                setRowDetailTextAndStatus(importDetails, row, $tm('import.nameonlyhasfirstname', '', true), 'warning');
            }
        } else if (hasExistingContestant && existingContestant.sourceParticipantId != null) {
            setRowDetailTextAndStatus(importDetails, row, $tm('import.willupdatecontestant'), 'warning');
        }
        // Info rows. Should be added last
        else if (hasExistingContestant && existingContestant.sourceParticipantId == null) {
            // We have an existing contestant
            setRowDetailTextAndStatus(
                importDetails,
                row,
                $ti('import.contestantexists', { name: importDetails.Name, bib: existingContestant.contestant?.Bib }, true),
                'info',
            );
        } else if (normalizedGroup?.generated) {
            // Group will be generated
            setRowDetailTextAndStatus(importDetails, row, $ti('import.groupgenerated', { group: normalizedGroup.value }, false), 'info');
        } else if (normalizedCategory.generated) {
            // Category will be generated
            setRowDetailTextAndStatus(
                importDetails,
                row,
                $ti('import.categorygenerated', { category: importDetails.Category }, false),
                'info',
            );
        } else if (normalizedTeam?.generated) {
            // Team will be generated
            setRowDetailTextAndStatus(importDetails, row, $ti('import.teamgenerated', { team: normalizedTeam.value }, false), 'info');
        }
    }

    // Add the details to the row
    row['details'] = importDetails;
};

/**
 * Helper function to set the row details text and the grid row status
 * @param importDetails
 * @param row
 * @param text
 * @param type
 */
const setRowDetailTextAndStatus = (importDetails: ContestantImportDetails, row: any, text: string, type: 'error' | 'warning' | 'info') => {
    if (type == 'error') {
        importDetails.ErrorMessages.push({ text: text, values: {} });
        importComp.value?.SetRowError(row, text);
    } else if (type == 'warning') {
        importDetails.WarningMessages.push({ text: text, values: {} });
        importComp.value?.SetRowWarning(row, text);
    } else if (type == 'info') {
        importDetails.InfoMessages.push({ text: text, values: {} });
        importComp.value?.SetRowInfo(row, text);
    }
};

/**
 * Checks if the required fields are met
 * The required fields are:
 * - Birthday or Year
 * - Gender
 * - FullnameLastFirst or FullnameFirstFirst or Firstname and Surname
 */
const _checkRequiredFields = () => {
    let missingFields = [];
    let missingRelayFields = [];
    let globalWarningMessage = '';

    // We need to know if it is a relay import. For relay import the Birthday and Gender are NOT required
    let isRelay = _getColumnMapfield('RelayTeamName') != null;

    // If we are only importing relay teams then we dont need to have the contestant fields, but we need to have the category field
    let isOnlyRelayImport = contestantImport.value.RelayTeamsOnly;

    if (!isOnlyRelayImport && _getColumnMapfield('Birthday') == null && _getColumnMapfield('Year') == null) {
        missingFields.push('import.missingdateofbirthoryear');
    }

    if (!isOnlyRelayImport && _getColumnMapfield('Gender') == null) {
        missingFields.push('import.gender');
    }

    // Find if we are missing firstname or surname
    if (
        !isOnlyRelayImport &&
        _getColumnMapfield('Firstname') == null &&
        _getColumnMapfield('Surname') == null &&
        _getColumnMapfield('FullnameLastFirst') == null &&
        _getColumnMapfield('FullnameFirstFirst') == null
    ) {
        missingFields.push('import.missingnamefields');
    } else if (!isOnlyRelayImport && _getColumnMapfield('FullnameLastFirst') == null && _getColumnMapfield('FullnameFirstFirst') == null) {
        if (_getColumnMapfield('Firstname') == null) {
            missingFields.push('import.firstname');
        }

        if (_getColumnMapfield('Surname') == null) {
            missingFields.push('import.surname');
        }
    }

    // Here we can not recognize a contestant if there is anything in the missingFields array
    if (missingFields.length > 0 && isResultImport.value) {
        // If we have a bib then we can ignore the missing fields
        if (_getColumnMapfield('Startnumber') != null) {
            missingFields = [];
        } else if (missingFields.length > 2) {
            // If we have a result import we need to have a startnumber
            missingFields.push('import.startnumber');
        }
    }

    // Check if we dont have any Races AND the GenerateRacesForGroups is not set
    if (!contestantImport.value.GenerateRacesForGroups && comStore.Groups.length == 0) {
        globalWarningMessage = 'import.missingraceinevent';
    }

    if (isRelay && !_getColumnMapfield('Category')) {
        missingRelayFields.push('import.category');
    }

    globalWarning.value = globalWarningMessage;
    missingRequiredFields.value = missingFields;
    missingRequiredRelayFields.value = missingRelayFields;

    return missingFields.length == 0;
};

/**
 * Contains all the formats that are available for the import as SelectLine
 */
const computedFormats = computed(() => {
    let formats: SelectLine[] = [];

    if (contestantImportSettings.value == null || contestantImportSettings.value.Formats === undefined) return formats;

    contestantImportSettings.value.Formats.forEach((format) => {
        formats.push({
            value: format.Name,
            text: format.Name!,
            subtext: formatDateDefault(parseDateStringISO(format.LastModified!), 'DATETIMESHORT'),
        });
    });

    // Sort by subtext as parsed date desc
    formats.sort((a, b) => {
        return parseDateStringISO(b.subtext!).getTime() - parseDateStringISO(a.subtext!).getTime();
    });

    return formats;
});

/**
 * Delete the selected format
 */
const deleteSelectedFormat = async () => {
    if (selectedFormat.value == null) {
        return;
    }
    // remove the format
    useDialog().YesNo({
        title: 'contestantimport.deleteformat',
        body: $ti('contestantimport.deleteformatconfirm', { format: selectedFormat.value }, true),
        yesCallback: async () => {
            contestantImportSettings.value?.RemoveFormat(selectedFormat.value!);
            selectedFormat.value = null;

            await saveStoredSettings(true);

            return true;
        },
    });
};

/**
 * Create a new format
 * Shows a modal for title
 */
const newFormat = () => {
    const format: any = { name: '' };

    useDialog().SaveCancel({
        title: 'contestantimport.newformattitle',
        body: NewImportFormat,
        saveCallback: async () => {
            await saveAndSelectNewFormat(format);
            return true;
        },
        componentProps: { modelValue: format, formatList: computedFormats.value },
        options: { Size: 'md' },
    });
};

/**
 * Save the current format
 */
const saveAndSelectNewFormat = async (format: { name: string }) => {
    if (format.name == null || format.name == '') {
        return;
    }

    if (computedFormats.value.find((x) => x.value?.toString().toLocaleLowerCase() == format.name.toLocaleLowerCase()) != null) {
        useToastStore().addError($tm('import.formatexists'));
        return;
    }

    let newFormat = new ContestantImportExtended(contestantImport.value, format.name, new Date(), columnMap.value);
    newFormat.GenderFemale = genderFemaleValue.value;
    newFormat.GenderMale = genderMaleValue.value;
    newFormat.GenderNeutral = genderNeutralValue.value;
    newFormat.DateFormat = dateFormatValue.value;
    newFormat.TimeFormat = timeFormatValue.value;
    newFormat.KnownFilenames = newFormat.KnownFilenames.concat(selectedFiles.value?.map((x) => x.name?.toLowerCase()) ?? []).filter(
        (v, i, a) => a.indexOf(v) === i,
    );
    newFormat.HasHeader = importComp.value?.HasHeader() ?? false;

    contestantImportSettings.value?.AddFormat(newFormat);
    selectedFormat.value = newFormat.Name;

    // We save it to the backend
    await saveStoredSettings();
};

/**
 * We save the settings to the backend
 * @param {boolean} deleteItem
 */
const saveStoredSettings = async (deleteItem: boolean = false) => {
    if (contestantImportSettings.value == null) return;

    setCurrentFormatProperty('GenderMale', genderMaleValue.value);
    setCurrentFormatProperty('GenderFemale', genderFemaleValue.value);
    setCurrentFormatProperty('GenderNeutral', genderNeutralValue.value);
    setCurrentFormatProperty('DateFormat', dateFormatValue.value);
    setCurrentFormatProperty('TimeFormat', timeFormatValue.value);
    setCurrentFormatProperty('ColumnMap', Object.fromEntries(columnMap.value));

    if (!deleteItem && contestantImportSettings.value.Formats?.length == 0) {
        // If we did not delete an item, we do not want to save an empty settings, we want to just return and not save anything
        return;
    }

    await useStoredSettings().SaveSettings<ContestantImportSettings>(
        ContestantImportSettings,
        contestantImportSettings.value,
        null,
        user?.ID,
        null,
    );
};

const setCurrentFormatProperty = (name: string, value: any) => {
    if (selectedFormat.value == null || contestantImportSettings.value == null || contestantImportSettings.value.Formats == undefined)
        return;

    let format = contestantImportSettings.value?.Formats.find((x) => x.Name == selectedFormat.value);

    if (format == null) return;

    Object.assign(format, { [name]: value });
};

const saveCurrentFormat = () => {};

/**
 * Set the selected format by name
 * if the name is null or the format is not found we will return
 * @param name
 */
const setImportSettings = (name: string | null) => {
    if (name == null || contestantImportSettings.value == null || !contestantImportSettings.value.Formats) return;

    let format = contestantImportSettings.value?.Formats.find((x) => x.Name == name);

    if (format == null) return;

    // We need to convert the columnmap to a Map<string, string> for the importComp
    // this is beacuase we cant serialize a Map<string, string> to the backend as JSON
    let convertedMap = new Map<string, string>();
    for (const key in format.ColumnMap) {
        convertedMap.set(key, format.ColumnMap[key]);
    }
    columnMap.value = convertedMap;

    // Convert the convertedMap to a ImportOption[]
    let options: { column: string; key: string }[] = [];
    options = Array.from(convertedMap).map(([key, value]) => {
        return { column: key, key: value };
    });

    const ci = ContestantImport.FromOther(format.ContestantImport);
    // We need to keep the localy assigned values
    ci.GUID = contestantImport.value.GUID;
    ci.EventID = contestantImport.value.EventID;
    ci.Locale = contestantImport.value.Locale;

    // Update the extended properties
    genderMaleValue.value = format.GenderMale ?? '';
    genderFemaleValue.value = format.GenderFemale ?? '';
    genderNeutralValue.value = format.GenderNeutral ?? '';
    dateFormatValue.value = format.DateFormat ?? '';
    timeFormatValue.value = format.TimeFormat ?? '';

    contestantImport.value = ci;

    importComp.value?.SetHasHeader(format.HasHeader ?? false);
    importComp.value?.SetHeaders(options);

    revalidateAll();
};

/**
 * This is a callback for when the import
 * @param done
 */
const onImportCompleted = async (done: boolean) => {
    importIsDone.value = done;

    if (done) {
        await contestantStore.ReloadContestants(); // Reload the contestants
        // We reset the import
        _resetImport();

        emits('importcompleted');
    }
};

// Whenever the importEmpty flag has changed we update the importComp.
// This mean that we will be able to import the rows, but we will skip all rows that are marked with errors
watch(
    () => contestantImport.value.IgnoreRowsWithErrors,
    (nv) => importComp.value?.SetIgnoreErrors(nv),
);

// When the GenerateCategories flag has changed we will update all the row details
// this is to pick up what new categories we need to generate and for what rows.
watch(
    () => contestantImport.value?.GenerateCategories,
    () => _updateAllRowDetails(),
);

// When the GenerateGroups flags is set we will update the row details
// this is to pick up what new groups we need to generate and for what rows.
watch(
    () => contestantImport.value?.GenerateGroups,
    () => _updateAllRowDetails(),
);

// We need to filter out the rows with exisiting contestants.
watch(
    () => contestantImport.value?.SkipExistingContestants,
    () => _updateAllRowDetails(),
);

// When the GenerateTeams flags is set we will update the row details
// this is to pick up what new teams we need to generate and for what rows.
watch(
    () => contestantImport.value?.GenerateTeams,
    () => _updateAllRowDetails(),
);

// When the RelayTeamsOnly flag is set we will update the row details
// This will use the relay team validations only and ignore the missing required fields for the individual contestants
watch(
    () => contestantImport.value?.RelayTeamsOnly,
    () => _updateAllRowDetails(),
);

// When the GenerateRacesForGroups flags is set we will update the row details
// this is to pick up the races that will be generated for the groups
watch(
    () => contestantImport.value?.GenerateRacesForGroups,
    () => {
        _checkRequiredFields();
        _updateAllRowDetails();
    },
);

// Whenever the selected format has changed we will update the import settings
watch(
    () => selectedFormat.value,
    (nv) => setImportSettings(nv),
);

watch(
    () => rowData.value,
    (nv, ov) => {
        if (nv.length > 0 && ov?.length == 0) {
            // This means that we have a new import
            setTimeout(() => {
                if (formatFromFilename != null && formatFromFilename.Name != null) {
                    selectedFormat.value = formatFromFilename?.Name ?? null;
                }
                if (currentImportFormat?.DataProvider == 'EQTiming' && currentImportFormat?.DataFormat == 'Custom') {
                    importComp.value?.ResizeColumnsToFitContent();
                }
            }, 100);
        }
    },
);

// Whenever the import settings has changed we will update the importComp
watch(
    contestantImportDefaultSettings,
    (nv) => {
        if (nv == null) return;

        genderFemaleValue.value = nv.GenderFemale ?? '';
        genderMaleValue.value = nv.GenderMale ?? '';
        genderNeutralValue.value = nv.GenderNeutral ?? '';
        dateFormatValue.value = nv.DateFormat ?? '';
        timeFormatValue.value = nv.TimeFormat ?? '';
    },
    { once: true },
);

/**
 * We check if there is any unknown countries and if there is we will show a warning with a mapper
 * When we have mapped the countries the mapping will be saved and we normalize the rows
 */
watchDebounced(
    unknownCountries.value,
    (nv) => {
        if (nv.size == 0) return;

        useDialog().SaveCancel({
            title: 'import.unknowncountries',
            body: CountryMapper,
            saveCallback: async () => {
                _updateAllRowDetails();
                return true;
            },
            componentProps: { sourceValues: Array.from(nv.values()) },
            vmodel: countryMappingValues,
            options: {
                Size: 'lg',
            },
        });
    },
    { debounce: 500 },
);

/** Will return an array of the automapkey split by comma
 * @param automapkey // Translation key
 */
const _parseLocaleAutomap = (automapkey: string) => {
    if (!automapkey) return [];

    return automapkey.split(',').map((x) => x.trim());
};

onMounted(async () => {
    contentComp.value?.loadingComp?.setModalSize('sm');

    const settings =
        (await useStoredSettings().GetSettings<ContestantImportSettings>(ContestantImportSettings, null, user?.ID, null)) ??
        new ContestantImportSettings();

    // This will give us a array of ContestantImportSettings, but the property Formatss will not be an instantiated class so we need to convert it to a class instance
    const formats = settings.Formats?.map((x) => {
        const ci = ContestantImport._FromServerJson(x.ContestantImport);
        return new ContestantImportExtended(ci, x.Name!, new Date(x.LastModified!), new Map<string, string>(Object.entries(x.ColumnMap)));
    });

    settings.Formats = formats;

    contestantImportSettings.value = settings;
});

// Set up all the options for the dropdowns in the import grid
const birthdayValidation = new DateValidator(dateFormat());
const genderValidation = new GenderValidator([...genderMale(), ...genderFemale(), ...genderNeutral()]);
const timeValidation = new TimeValidator(timeFormat());
const options = new ImportOptions<ContestantImportRow>([
    new ImportOption('Skip', 'import.donotimport'),
    new ImportOption('Firstname', 'import.firstname', null, _parseLocaleAutomap($tm('import.firstnameautomap'))),
    new ImportOption('Surname', 'import.surname', new NotEmptyValidator(), _parseLocaleAutomap($tm('import.surnameautomap'))),
    new ImportOption(
        'FullnameLastFirst',
        'import.fullnamesurnamefirst',
        new NotEmptyValidator(),
        _parseLocaleAutomap($tm('import.fullnamesurnamefirstautomap')),
    ),
    new ImportOption(
        'FullnameFirstFirst',
        'import.fullnamefirstnamefirst',
        new NotEmptyValidator(),
        _parseLocaleAutomap($tm('import.fullnamefirstnamefirstautomap')),
    ),
    new ImportOption('Gender', 'import.gender', genderValidation, _parseLocaleAutomap($tm('import.genderautomap'))),
    new ImportOption('Birthday', 'import.dateofbirth', birthdayValidation, _parseLocaleAutomap($tm('import.dateofbirthautomap'))),
    new ImportOption('Year', 'import.year', new YearValidator(), _parseLocaleAutomap($tm('import.yearautomap'))),
    new ImportOption('Month', 'import.month', new MonthValidator(), _parseLocaleAutomap($tm('import.monthautomap'))),
    new ImportOption('Day', 'import.day', new DayValidator(), _parseLocaleAutomap($tm('import.dayautomap'))),
    new ImportOption('Nationality', 'import.nationality', null, _parseLocaleAutomap($tm('import.nationalityautomap'))),
    new ImportOption('import.event', null, null, null, [
        new ImportOption('Club', 'import.club', null, _parseLocaleAutomap($tm('import.clubautomap'))),
        new ImportOption('Category', 'import.category', null, _parseLocaleAutomap($tm('import.categoryautomap'))),
        new ImportOption('Group', 'import.group', null, _parseLocaleAutomap($tm('import.groupautomap'))),
        new ImportOption('Startnumber', 'import.startnumber', null, _parseLocaleAutomap($tm('import.startnumberautomap'))),
        new ImportOption('Starttime', 'import.startingtime', timeValidation, _parseLocaleAutomap($tm('import.startingtimeautomap'))),
        new ImportOption('Endtime', 'import.endingtime', timeValidation, _parseLocaleAutomap($tm('import.endingtimeautomap'))),
        new ImportOption('EventTeamName', 'import.team', null, _parseLocaleAutomap($tm('import.teamautomap'))),
        new ImportOption('Seeding', 'import.seedingvalue', null, _parseLocaleAutomap($tm('import.seedingvalueautomap'))),
    ]),
    new ImportOption('import.address', null, null, null, [
        new ImportOption('PostAddress1', 'import.postaladdress', null, _parseLocaleAutomap($tm('import.postaladdressautomap'))),
        new ImportOption('PostAddress2', 'import.postaladdress2', null, _parseLocaleAutomap($tm('import.postaladdress2automap'))),
        new ImportOption('PostZipCode', 'import.postalcode', null, _parseLocaleAutomap($tm('import.postalcodeautomap'))),
        new ImportOption('PostCity', 'import.towncity', null, _parseLocaleAutomap($tm('import.towncityautomap'))),
        new ImportOption('CountryId', 'import.country', null, _parseLocaleAutomap($tm('import.countryautomap'))),
        new ImportOption('Company', 'import.company', null, _parseLocaleAutomap($tm('import.companyautomap'))),
    ]),
    new ImportOption('import.contact', null, null, null, [
        new ImportOption('Email', 'import.email', new EmailValidator(false), _parseLocaleAutomap($tm('import.emailautomap'))),
        new ImportOption('Telephone', 'import.telephone', null, _parseLocaleAutomap($tm('import.telephoneautomap'))),

        new ImportOption('RegistrarEmail', 'import.registraremail', null, _parseLocaleAutomap($tm('import.registraremailautomap'))),
    ]),
    new ImportOption('import.relay', null, null, null, [
        new ImportOption('RelayTeamName', 'import.relayteamname', null, _parseLocaleAutomap($tm('import.relayteamnameautomap'))),
        new ImportOption('LegNumber', 'import.legnumber', null, _parseLocaleAutomap($tm('import.legnumberautomap'))),
    ]),
    new ImportOption('import.additional', null, null, null, [
        new ImportOption('Chip1', 'import.chip1', null, _parseLocaleAutomap($tm('import.chip1automap'))),
        new ImportOption('Chip2', 'import.chip2', null, _parseLocaleAutomap($tm('import.chip2automap'))),
    ]),
    new ImportOption('import.extra', null, null, null, [
        new ImportOption('Extra1', 'import.extra1', null, _parseLocaleAutomap($tm('import.extra1automap'))),
        new ImportOption('Extra2', 'import.extra2', null, _parseLocaleAutomap($tm('import.extra2automap'))),
        new ImportOption('Extra3', 'import.extra3', null, _parseLocaleAutomap($tm('import.extra3automap'))),
        new ImportOption('Extra4', 'import.extra4', null, _parseLocaleAutomap($tm('import.extra4automap'))),
        new ImportOption('Extra5', 'import.extra5', null, _parseLocaleAutomap($tm('import.extra5automap'))),
    ]),

    // ID Types
    new ImportOption('import.ids', null, null, null, [
        new ImportOption('BUYPASSID', 'import.buypassid', null, _parseLocaleAutomap($tm('import.buypassidautomap'))),
        new ImportOption('NIFID', 'import.nifid', null, _parseLocaleAutomap($tm('import.nifidautomap'))),
        new ImportOption('EMITID', 'import.emitid', null, _parseLocaleAutomap($tm('import.emitidautomap'))),
    ]),

    new ImportOption('import.results', null, null, null, [
        new ImportOption('Rank', 'import.rank', new NotEmptyValidator(), _parseLocaleAutomap($tm('import.rankautomap'))),
        new ImportOption('ResultTime', 'import.resulttime', timeValidation, _parseLocaleAutomap($tm('import.resulttimeautomap'))),
        new ImportOption('Resultpoint', 'import.resultpoint', null, _parseLocaleAutomap($tm('import.resultpointautomap'))),
    ]),

    // These are just helper mappers. They are not visible in the actual dropdown, but are used to map internally
    new ImportOption(
        'import.readonly',
        null,
        null,
        null,
        [
            // new ImportOption('_SourceCategory', 'import.sourcecategory', null),
            new ImportOption('_SourceCategoryName', 'import.sourcecategoryname', null),
            new ImportOption('_SourceGroupName', 'import.sourcegroupname', null),
            new ImportOption('_SourceClubID_', 'import.sourceclubid', null),
            // new ImportOption('_SourceCategoryID', 'import.sourcecategoryid', null),
            // new ImportOption('_SourceParticipantID', 'import.sourceparticipantid', null),
        ],
        null,
        false,
    ),
] as Array<ImportOption>);
</script>
